import { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

// Decorative sparkle component (matches LandingPage/PaymentPage style)
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => {
  // Simple CSS animation since we can't use framer-motion in class component render
  const animationStyle = {
    animation: `sparkle-pulse 2s ease-in-out infinite`,
    animationDelay: `${delay}s`,
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      className={className}
      style={animationStyle}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
    </svg>
  )
}

// Sad face skincare bottle illustration
const SadBottleIcon = () => (
  <svg width="80" height="100" viewBox="0 0 80 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Bottle cap */}
    <rect x="28" y="4" width="24" height="12" rx="2" fill="#46C0C6" />
    {/* Bottle body */}
    <path
      d="M24 24 L24 16 L56 16 L56 24 L60 32 L60 88 Q60 92 56 92 L24 92 Q20 92 20 88 L20 32 Z"
      stroke="#46C0C6"
      strokeWidth="3"
      fill="#E8F7F8"
    />
    {/* Sad face - eyes */}
    <circle cx="32" cy="52" r="4" fill="#46C0C6" />
    <circle cx="48" cy="52" r="4" fill="#46C0C6" />
    {/* Sad face - frown */}
    <path d="M30 72 Q40 64 50 72" stroke="#46C0C6" strokeWidth="3" fill="none" strokeLinecap="round" />
    {/* Tear drop */}
    <path d="M32 60 Q30 66 32 70 Q34 66 32 60" fill="#7EC8E3" />
  </svg>
)

/**
 * Error Boundary to catch React rendering errors
 * Shows user-friendly error message and logs details for debugging
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console for debugging
    console.error('[ErrorBoundary] Caught error:', error.message)
    console.error('[ErrorBoundary] Stack:', error.stack)
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack)

    // Store for display
    this.setState({ errorInfo })

    // Also log to localStorage for mobile debugging
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
      }
      localStorage.setItem('k-skin-last-error', JSON.stringify(errorLog))
    } catch {
      // Ignore localStorage errors
    }
  }

  handleRefresh = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.reload()
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isDev = import.meta.env.DEV

      return (
        <div className="min-h-screen bg-[#FFFEF1] flex flex-col overflow-hidden">
          {/* CSS for sparkle animation */}
          <style>{`
            @keyframes sparkle-pulse {
              0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.7; }
              50% { transform: scale(1.3) rotate(15deg); opacity: 1; }
            }
          `}</style>

          {/* Header */}
          <header className="py-5 px-4">
            <h1
              className="text-[18px] text-center"
              style={{ fontFamily: "'Oleo Script', cursive" }}
            >
              <span className="text-[#688192]">K-Beauty</span>
              <span className="text-[#46C0C6]"> AI</span>
            </h1>
          </header>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-6 relative">
            {/* Decorative sparkles */}
            <Sparkle className="absolute top-16 left-8" size={20} color="#FF8C33" delay={0} />
            <Sparkle className="absolute top-24 right-12" size={16} color="#E8A87C" delay={0.5} />
            <Sparkle className="absolute bottom-32 left-16" size={14} color="#46C0C6" delay={1} />
            <Sparkle className="absolute bottom-48 right-8" size={18} color="#7EC8E3" delay={0.3} />

            <div className="max-w-sm w-full text-center">
              {/* Illustration */}
              <div className="mb-6 flex justify-center">
                <SadBottleIcon />
              </div>

              {/* Message */}
              <h1
                className="text-[24px] text-black mb-3"
                style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 600 }}
              >
                Oops! Something{' '}
                <span className="text-[#FF8C33]">spilled</span>
              </h1>
              <p className="text-[#688192] mb-8 leading-relaxed text-[15px]">
                Don't worry, your skin data is safe!
                <br />
                Let's get you back on track.
              </p>

              {/* Actions */}
              <div className="flex flex-col gap-3 max-w-[280px] mx-auto">
                <button
                  onClick={this.handleRefresh}
                  className="w-full bg-[#4CD964] hover:bg-[#3DBB53] text-white px-6 py-4 rounded-full font-semibold text-[16px] active:scale-[0.98] transition-all shadow-sm"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-white hover:bg-gray-50 text-[#688192] px-6 py-4 rounded-full font-semibold text-[16px] active:scale-[0.98] transition-all border-2 border-[#E5E5E5]"
                >
                  Back to Home
                </button>
              </div>

              {/* Debug info - only in development */}
              {isDev && this.state.error && (
                <details className="mt-8 text-left">
                  <summary className="text-xs text-[#688192] cursor-pointer hover:text-[#46C0C6]">
                    Developer Details
                  </summary>
                  <div className="mt-2 bg-white/80 p-3 rounded-xl overflow-auto max-h-40 border border-[#E5E5E5]">
                    <p className="text-[#688192] font-mono text-xs break-all">
                      {this.state.error.message}
                    </p>
                    {this.state.errorInfo && (
                      <pre className="mt-2 text-[#A0A0A0] font-mono text-[10px] whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>

          {/* Footer */}
          <footer className="py-4 px-4 text-center">
            <p className="text-xs text-[#A0A0A0]">
              If this keeps happening, please contact{' '}
              <a href="mailto:support@k-skin.ai" className="text-[#46C0C6] hover:underline">
                support@k-skin.ai
              </a>
            </p>
          </footer>
        </div>
      )
    }

    return this.props.children
  }
}
